import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { fileDealDocuments } from '@/services/deals.service.js';

// THE CARRIER'S DOCUMENT, HANDED TO A CONFIRM DOOR (paperwork-1 1b, L1b-B2). The confirm forms (the carrier approved an
// endorsement, the carrier confirmed an LPR endorsement, the carrier confirmed a cancellation) may carry the carrier's PDF. It is
// filed through the deal's document door first, as the carrier's endorsement of THIS ledger row (fileDealDocuments: fileDocument,
// then the one link writer), so "Does this replace <name>?" is asked for this row's documents and a link refusal is heard before
// the carrier's answer is recorded; the confirm door is then handed the documents id the door answered (carrier_document_id).
// documentId() answers null (no file picked), the id, or ASKED (the server asked the replace question: the form shows it and
// waits for the broker to answer and press the confirm button again). A refusal throws with the server's words.
export const ASKED = Symbol('carrier-doc-replace-asked');

export function useCarrierDocPick({ dealId, txnId, rowName }) {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [ask, setAsk] = useState(null); // the candidates the server named
  const [answer, setAnswer] = useState(undefined); // a candidate id, or 'none'
  const [filed, setFiled] = useState(null); // { id, name, file }: filed once, never twice

  function pick(f) {
    setFile(f || null); setAsk(null); setAnswer(undefined); setFiled(null);
    if (input.current) input.current.value = '';
  }

  async function documentId() {
    if (!file) return null;
    if (filed && filed.file === file) return filed.id;
    const body = await fileDealDocuments(dealId, [{ file, kind: 'endorsement', transaction_id: txnId, replaces_document_id: answer }]);
    const r = body.results[0] || {};
    if (r.code === 'REPLACES_UNANSWERED') { setAsk(r.candidates || []); return ASKED; }
    if (!r.ok) throw new Error(r.error || `${file.name} was not filed`);
    if (r.document?.transaction?.id !== txnId) throw new Error(`${r.document?.name || file.name} was filed but did not read back attached to ${rowName}`);
    setFiled({ id: r.document.id, name: r.document.name, file });
    setAsk(null);
    return r.document.id;
  }

  const field = (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-carrier-doc-pick={txnId}>
      <input ref={input} type="file" accept="application/pdf" className="hidden" data-carrier-doc-input={txnId} onChange={(e) => pick(e.target.files?.[0])} />
      {file ? (
        <>
          <span className="text-text" data-carrier-doc-picked>
            {filed ? `${file.name}, filed as ${filed.name}` : `${file.name} will be filed as the carrier's document for ${rowName}`}
          </span>
          <Button size="sm" variant="ghost" onClick={() => pick(null)}>Remove {file.name}</Button>
        </>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => input.current?.click()}>Add the carrier's document for {rowName} (PDF)</Button>
      )}
      {ask ? (
        <div className="flex w-full flex-wrap items-center gap-2" data-carrier-doc-replace>
          <span className="text-text">Does {file?.name} replace {ask.map((c) => c.name).join(' or ')}? Answer, then confirm again.</span>
          {ask.map((c) => (
            <Button key={c.id} size="sm" variant={answer === c.id ? 'default' : 'outline'} onClick={() => setAnswer(c.id)}>Yes, it replaces {c.name}</Button>
          ))}
          <Button size="sm" variant={answer === 'none' ? 'default' : 'outline'} onClick={() => setAnswer('none')}>No, it is another document</Button>
        </div>
      ) : null}
    </div>
  );

  return { field, documentId, picked: !!file };
}
